import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlass, UserPlus, ArrowRight, CaretLeft, CaretRight, Trash, WhatsappLogo } from "@phosphor-icons/react";
import { whatsappClient } from "@/lib/whatsapp";

const fmtDate = (d) => { const p = new Date(d); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";

export default function Clienti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phone2, setPhone2] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (query = q) => {
    const { data } = await api.get("/clients", { params: query ? { q: query } : {} });
    setClients(data);
  };

  useEffect(() => {
    setPage(1);
    const t = setTimeout(() => load().catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(clients.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageClients = clients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageNumbers = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2) pageNumbers.push(p);
  }

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", { name, phone, phone2, email, notes });
      toast.success("Cliente aggiunto");
      setName(""); setPhone(""); setPhone2(""); setEmail(""); setNotes(""); setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.name}"? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="clienti-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Anagrafica</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Clienti</h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="toggle-client-form-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150">
          <UserPlus size={17} /> Nuovo cliente
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-border rounded-md bg-card p-6 grid grid-cols-1 md:grid-cols-6 gap-4 items-end" data-testid="client-form">
          <div className="md:col-span-1">
            <label className={labelCls}>Nome e cognome *</label>
            <input required value={name} onChange={(e) => setName(e.target.value.replace(/\d/g, ""))} data-testid="client-name-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Telefono *</label>
            <input required inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))} data-testid="client-phone-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Telefono 2</label>
            <input inputMode="tel" value={phone2} onChange={(e) => setPhone2(e.target.value.replace(/[^\d+]/g, ""))} data-testid="client-phone2-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="client-email-input" className={inputCls} />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note" data-testid="client-notes-input" className={inputCls} />
            <button type="submit" disabled={saving} data-testid="client-submit-button"
              className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity duration-150 shrink-0">
              Salva
            </button>
          </div>
        </form>
      )}

      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search-input"
          className={`${inputCls} pl-10`} placeholder="Cerca per nome o telefono..." />
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Nome</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Telefono</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Email</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Cliente dal</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground" data-testid="clients-empty">Nessun cliente trovato</td></tr>
            )}
            {pageClients.map((c) => (
              <tr key={c.id} className="hover:bg-accent/40 transition-colors duration-150" data-testid={`client-row-${c.id}`}>
                <td className="px-5 py-3.5 font-semibold">{c.name}</td>
                <td className="px-5 py-3.5">{c.phone}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{c.email || "—"}</td>
                <td className="px-5 py-3.5 text-muted-foreground">{fmtDate(c.created_at)}</td>
                <td className="px-5 py-3.5 text-right">
                  <div className="inline-flex items-center gap-3">
                    {c.phone && /\d/.test(c.phone) && (
                      <button onClick={() => whatsappClient(c)} title={`Contatta ${c.name} su WhatsApp`} data-testid={`client-whatsapp-${c.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-green-500/50 text-green-500 hover:bg-green-500/10 transition-colors duration-150">
                        <WhatsappLogo size={16} weight="fill" />
                      </button>
                    )}
                    <Link to={`/clienti/${c.id}`} data-testid={`client-detail-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity duration-150">
                      Scheda <ArrowRight size={14} />
                    </Link>
                    {isAdmin && (
                      <button onClick={() => removeClient(c)} data-testid={`client-delete-${c.id}`}
                        className="text-muted-foreground hover:text-destructive transition-colors duration-150">
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clients.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="clients-pagination">
          <p className="text-xs text-muted-foreground" data-testid="clients-pagination-info">
            Pagina {currentPage} di {totalPages} — {clients.length} clienti
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} data-testid="clients-prev-page"
              className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30 transition-colors duration-150">
              <CaretLeft size={15} />
            </button>
            {pageNumbers.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-1.5">
                {i > 0 && pageNumbers[i - 1] < p - 1 && (
                  <span className="text-muted-foreground text-xs px-1">…</span>
                )}
                <button onClick={() => setPage(p)} data-testid={`clients-page-${p}`}
                  className={`w-9 h-9 rounded-md border text-sm font-semibold transition-colors duration-150 ${
                    p === currentPage
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
                  {p}
                </button>
              </span>
            ))}
            <button onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} data-testid="clients-next-page"
              className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30 transition-colors duration-150">
              <CaretRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
